import { Injectable, OnDestroy } from '@angular/core';
import { OfficeRoute as OfficeStartRoute } from '@office-start/officehome-types';
import { logError } from '@office-start/telemetry-service';
import { OfficeThemeMode } from '@office-start/theming-types';
import { HarmonyTheme, HarmonyThemeMode, VisualGates } from '@office-start/visual-types';
import { combineLatest, from, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { OfficeRoute } from '../constants/office-route.enum';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ConfigParserService } from './config-parser.service';
import { LocalStorageService } from './local-storage.service';
import { OfficeHomeStorageService } from './office-home-storage.service';
import { OfficeRouteService } from './office-route.service';
import { ThemingService } from './theming.service';

export interface VisualConfigEvent {
    visualInitialized: boolean;
    visualBackgroundEnabled: boolean;
    visualOfficeWrapperStyle: { [key: string]: string };
}

// User Storage key for Harmony theme, also declared in OfficeStart
const HARMONY_THEME_USER_STORAGE_KEY = 'HarmonyTheme';

@Injectable({
    providedIn: 'root'
})
export class VisualService implements OnDestroy {
    public visualConfigSubscription: Observable<VisualConfigEvent>;
    private harmonyThemeSubscription: Subscription;
    private readonly visualModuleObservable: Observable<typeof import('@office-start/visual-service')>;
    private readonly visualDataModuleObservable: Observable<typeof import('@office-start/visual-data-service')>;
    private userPersonalizationAllowed?: boolean;

    constructor(
        protected readonly capabilitiesService: CapabilitiesService,
        protected readonly clientStartupService: ClientStartupConfigService,
        protected readonly configParserService: ConfigParserService,
        protected readonly localStorageService: LocalStorageService,
        protected readonly routeService: OfficeRouteService,
        protected readonly themingService: ThemingService,
        protected readonly userDataStorageService: OfficeHomeStorageService
    ) {
        if (!capabilitiesService.capabilities.visualEnabled) {
            // Create empty observable so it can still be referenced/subscribed from other modules even when things aren't enabled.
            this.visualConfigSubscription = Observable.create();
            return;
        }

        // Create behavior subject for loading office start module
        // Import here to prevent chunk load errors in UWP Spartan Edge that result from referencing
        // fluent packages that support features that are not supported in Spartan Edge
        try {
            this.visualModuleObservable = from(import('@office-start/visual-service'));
            this.visualDataModuleObservable = from(import('@office-start/visual-data-service'));
        } catch (err) {
            logError({
                eventName: 'VisualServiceOfficeStartChunkLoadError'
            });
        }

        this.visualConfigSubscription = combineLatest(this.visualModuleObservable, this.visualDataModuleObservable, this.themingService.themeShades, this.themingService.darkModeSubscription, this.routeService.getRouteChangeEndObservable())
            .pipe(map(([visualModule, visualDataModule, themeEvent, darkModeEvent, routeEvent]) => {
                const themeId = ((themeEvent.theme.themeId?.toLowerCase() === ThemingService.baseThemeId || themeEvent.theme.themeId === ThemingService.staticDarkTheme) && themeEvent.theme.reactShellTheme.PreferOfficeTheme) || themeEvent.theme.themeId === ThemingService.baseWithOfficeThemePreferredThemeId ? ThemingService.officeThemeId : themeEvent.theme.themeId;
                const visualBackgroundEnabled = visualModule?.isVisualBackgroundEnabled(
                    this.clientStartupService.config.authType === AuthType.MSA,
                    window.matchMedia('(-ms-high-contrast: active)').matches || window.matchMedia('(forced-colors: active)').matches,
                    visualModule?.getRouteFlight(routeEvent.currentRoute.route as OfficeStartRoute),
                    themeId,
                    darkModeEvent ? OfficeThemeMode.dark : OfficeThemeMode.default,
                    this.getVisualGates()
                );

                // Update theme on harmony theme change trigger
                if (!this.harmonyThemeSubscription && visualDataModule && this.capabilitiesService.capabilities.visualSettingEnabled) {
                    this.harmonyThemeSubscription = visualDataModule.harmonyThemeObservable.subscribe(harmonyThemeEvent => {
                        this.updateTheme(harmonyThemeEvent);
                    });
                }

                return {
                    visualBackgroundEnabled,
                    visualInitialized: visualModule !== undefined && visualDataModule !== undefined,
                    visualOfficeWrapperStyle: this.getVisualOfficeWrapperStyle(routeEvent.currentRoute.route)
                };
            }));

        this.initializeHarmonyTheme();
    }

    public ngOnDestroy() {
        this.harmonyThemeSubscription?.unsubscribe();
    }

    protected getVisualGates(): VisualGates {
        return {
            visualAppsModuleEnabled: this.capabilitiesService.capabilities.visualAppsModuleEnabled,
            visualBackgroundEnabled: this.capabilitiesService.capabilities.visualBackgroundEnabled,
            visualCreateCenterModuleEnabled: this.capabilitiesService.capabilities.visualCreateCenterModuleEnabled,
            visualEnabled: this.capabilitiesService.capabilities.visualEnabled,
            visualFeedModuleEnabled: this.capabilitiesService.capabilities.visualFeedModuleEnabled,
            visualMyContentModuleEnabled: this.capabilitiesService.capabilities.visualMyContentModuleEnabled,
            visualSettingEnabled: this.capabilitiesService.capabilities.visualSettingEnabled
        };
    }

    protected getVisualOfficeWrapperStyle(route: OfficeRoute): { [key: string]: string } {
        if (route === OfficeRoute.OfficeHome || route === OfficeRoute.Pwa || route === OfficeRoute.Create) {
            return {
                position: 'relative'
            };
        }
        return {};
    }

    protected initializeHarmonyTheme() {
        if (!this.capabilitiesService.capabilities.visualSettingEnabled) {
            return;
        }

        // $TODO: For later stages, we'll need to initiale the correct value here based on mode. If user doesn't have any settings
        // we should infer from the desktop app. When removing this code, also uncomment the code in ShellBootstrap.
        // For now, since we're respecting desktop settings, do not set or initialize the HarmonyTheme here
        if (this.capabilitiesService.capabilities.pwaEnabled) {
            return;
        }

        // Initialize Harmony theme
        if (this.capabilitiesService.capabilities.pwaEnabled && this.capabilitiesService.capabilities.isConsumerUser) {
            this.initializeHarmonyThemeLocalStorage();
        } else {
            this.initializeHarmonyThemeUserStorage();
        }
    }

    protected initializeHarmonyThemeUserStorage() {
        this.userDataStorageService
            .get<HarmonyTheme>(HARMONY_THEME_USER_STORAGE_KEY, 1)
            .then(harmonyTheme => {
                this.updateTheme(harmonyTheme);
            })
            .catch(_error => {
                // ToDo: Log error
            });
    }

    protected initializeHarmonyThemeLocalStorage() {
        const harmonyTheme = this.localStorageService.get(HARMONY_THEME_USER_STORAGE_KEY);
        this.updateTheme(harmonyTheme);
    }

    protected updateTheme(harmonyTheme: HarmonyTheme | undefined) {
        if (harmonyTheme && (harmonyTheme?.preferHarmonyTheme || this.clientStartupService.config.authType === AuthType.MSA)) {
            this.initializeUserPersonalizationSetting();
            this.themingService.updateVisualsTheme(harmonyTheme.id, harmonyTheme.mode === HarmonyThemeMode.Dark, this.userPersonalizationAllowed);
        }
    }

    private initializeUserPersonalizationSetting () {
        if (this.userPersonalizationAllowed !== undefined) {
            return;
        }

        // Disable personalization for consumer
        if (this.clientStartupService.config.authType === AuthType.MSA) {
            this.userPersonalizationAllowed = true;
            return;
        }

        // Check if personalization is enabled for commercial
        // ToDo: When integrating with Shell themes in custom settings pane in commercial, the 'UserPersonalizationAllowed' setting will need to be
        // taken into account and respected when calls to this shim API are made.
        // When UserPersonalizationAllowed is set to false only the default and contrast themes will be made available in the default Shell settings pane:
        // https://learn.microsoft.com/en-us/microsoft-365/admin/setup/customize-your-organization-theme
        try {
            const navBarDataElement = this.capabilitiesService.capabilities.pwaEnabled ? 'navBarData' : 'navbarData';
            const navBarData = this.configParserService.parse<{ ClientData?: string }>(navBarDataElement);
            if (navBarData?.ClientData) {
                const { UserPersonalizationAllowed } = JSON.parse(navBarData.ClientData);
                this.userPersonalizationAllowed = UserPersonalizationAllowed ?? false;
                return;
            }
        } catch (_e) {
            // ToDo: log
        }
        this.userPersonalizationAllowed = false;
    }
}
